// İSG Portal — Güvenlik / Saha Geçişi (Security Entry — kapı kodu / TC) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §29. Backend kanonu; burada **varsayım YOK** — endpoint yolları,
// istek/yanıt alanları ve kimlik kuralları doğrudan sözleşmeden türetilmiştir. Serileştirme
// camelCase'dir (§1).
//
// **Kapsam.** Bu istemci yalnız GÜVENLİK (`security`) uçlarını kapsar — personel
// (supplier-self) uçları (§29.4, `/supplier/me/entry-qr` + `/supplier/me/entry-scan`)
// `mobile-supplier`'ın kendi isteğidir, burada YOKTUR (uygulama sınırı, `apps/mobile-supplier`
// zaten `@isgportal/shared` kullanmaz). Akış A (personel giriş QR'ını üretir, güvenlik KAMERAYLA
// okur — `POST /security/entry/verify`) S32 planında istemci tarafı ERTELENMİŞTİ; brief 092 ile
// `apps/web-expert` güvenlik masasına (kamera decode + elle yapıştırma yedeği) eklendiği için
// `verify` ucu ARTIK burada modellenir (aşağıda `EntryVerifyRequest` + `EntryVerifyResultDto` +
// `verify`). KVKK: yanıt yalnız ad + işveren + karar taşır; kamera decode'u YERELDİR — sunucuya
// yalnız çözülen opak `qrToken` gider, görüntü DEĞİL.
//
// **Yeni aksiyon (`gate-result`, K4/fail-closed).** `gate-result` + `entries` uçları YENİ bir
// aksiyonun (`[ModuleAction("security","gate-result")]`) arkasındadır ve seed'de **grant'sız**
// gelir — mevcut `get` grant'ı bu ikisini AÇMAZ (§29 girişi). İstemci `403 FORBIDDEN`'ı bu
// yüzden ayrı bir UX durumu olarak ele almalıdır (bkz. `apps/web-expert/src/lib/securityAccess.ts`
// ve gate/day sekmelerinin kendi 403 işleyişi).
//
// **`contractorName` (S32c) — YAYINDA, backend uygulandı ve doğrulandı** (1627 test yeşil,
// canlı Postgres). Sözleşme (api-contract.md §29.5) alanı **dört** uçta taşır — `GateScanDto`,
// `TcLookupResultDto`, `SecurityEntryRowDto` VE `EntryVerifyResultDto`; DÖRDÜ de burada modellenir
// (`verify` istemcisi brief 092 ile eklendi — yukarıdaki "Kapsam" notu). Alan `string | null`'dır
// ve backend GERÇEKTEN `null` döner —
// `undefined` DEĞİL, `""` DEĞİL. `null`'ın üç meşru sebebi vardır, hiçbiri hata değildir:
// (1) personel firmaya FİRMA DÜZEYİNDE bağlıdır, bir taşerona değil (desteklenen bir durum —
// dev seed'i de budur); (2) taşeron soft-delete edilmiştir; (3) atama satırı, geçiş log'u
// yazıldıktan SONRA kaldırılmıştır. Okuyan ekranlar `null`'ı satırı/kartı GİZLEMEK için
// kullanmaz — her yüzeyin kendi render kuralı vardır (bkz. `VerdictCard.tsx`/`DayListTab.tsx`).
//
// **KVKK.** TC istekte/yanıtta hiçbir zaman kalıcı loglanmaz; `tc` GÖVDEDE taşınır (asla query
// string'e girmez — LB/proxy access-log + `Referer` sızıntısı). `personnelName` yalnız ad+soyad;
// TC/telefon hiçbir yanıtta yoktur.

import { api } from './client';
import type { PagedResult } from './envelope';

/** İzin hâli reddi — §29.1(a). Yalnız izin değerlendirmesinden gelir (jeton hâli DEĞİL). */
export type SecurityPermitDenyReason =
  | 'NoPermit'
  | 'OutOfSite'
  | 'EntryNotPermitted'
  | 'OutOfValidity'
  | 'Terminal';

/**
 * Jeton hâli reddi — §29.1(b). Yalnız okutulan KODUN hâlini anlatır; izin hiç
 * değerlendirilmez. Bir yanıtta izin ailesinden bir kodla ASLA bir arada bulunmaz.
 */
export type SecurityTokenDenyReason = 'TokenInvalid' | 'TokenExpired' | 'TokenConsumed';

/**
 * Geçiş yöntemi — §29.3/§29.5. `GateQr` personelin KENDİ BEYANIDIR (yakınlık/cihaz/konum
 * bağı yok — ekrandaki kod fotoğraflanıp uzaktan okutulabilir); `PersonnelQr`/`TcLookup`
 * güvenlik-aracılıdır. Herhangi bir sayım/tahliye görünümü bunu AYIRT ETMEK ZORUNDADIR.
 */
export type SecurityEntryMethod = 'PersonnelQr' | 'GateQr' | 'TcLookup';

/** `GET /security/sites` satırı — §29.5. Yalnız `{ uuid, name }` (adres/şube dönmez). */
export interface SecuritySiteDto {
  uuid: string;
  name: string;
}

/** Kapı/saha QR isteği — §29.5 (`POST /security/entry-qr`). `siteUuid` ZORUNLU (kapı bir sahaya aittir). */
export interface CreateGateQrRequest {
  siteUuid: string;
}

/** Jeton üretim yanıtı — §29.2/§29.5 (`gate-qr`). TTL ~120 sn; istemci TTL dolmadan yeniler. */
export interface EntryTokenDto {
  qrToken: string;
  /** UTC ISO 8601 (§4). */
  expiresAt: string;
}

/** TC ile sorgu isteği — §29.5 (`POST /security/entry/tc-lookup`). `tc` GÖVDEDE (KVKK). */
export interface TcLookupRequest {
  tc: string;
  siteUuid: string;
}

/**
 * TC ile sorgu yanıtı — §29.5. `found=false` bu firmada kayıtlı kimse yok demektir —
 * **NÖTR bir durumdur, ret DEĞİLDİR** (en sık sebep yanlış yazılmış bir hane). `siteName`
 * yanıtta YOKTUR (saha girdiden zaten bilinir).
 */
export interface TcLookupResultDto {
  found: boolean;
  personnelName: string | null;
  /**
   * YENİ (S32c) — personelin (çağıranın firmasındaki) TAŞERONU, §29.5'in dört-uçta-ortak
   * `contractorName` kuralı. `found=false` iken DAİMA `null` (kimse çözülmedi). `null`'ın
   * başka meşru sebepleri de vardır (firma-düzeyi bağ, taşeron soft-delete, log yazıldıktan
   * SONRA kaldırılmış atama) — **hiçbiri hata değildir**; `null`'ı bir ret sinyali gibi OKUMAZ
   * ve hiçbir sayımda KULLANMAZ. Render kuralı İKİ yüzeyde de AYNI (S32c §13.5 düzeltmesi —
   * dokümanın kendi kuralı: bir em-dash yalnız "uygulanamaz" der, "bilinmiyor" DEĞİL, ve
   * `contractorName=null` "sunucu çözemedi" demektir): `null` HİÇBİR GLİF/tire üretmez —
   * `VerdictCard.tsx` satırı REZERVE EDİLMİŞ yükseklikle boş bırakır (dört-satır geometrisi
   * glifin değil satırın varlığıyla sabit kalır), `DayListTab.tsx` satırı tamamen OMİTİR
   * (dev seed'in her satırda `null` olduğu bir tabloda bir sütun tamamı gürültü olurdu).
   * Bilerek `string | null` —
   * `?? ''` YOK (backend'in §14/§17.3'teki `?? string.Empty` alışkanlığı burada BİLİNÇLİ olarak
   * devralınmaz).
   */
  contractorName: string | null;
  allowed: boolean;
  permitTitle: string | null;
  reason: SecurityPermitDenyReason | null;
}

/** Personel QR doğrulama isteği — §29.5 (`POST /security/entry/verify`, Akış A). `qrToken`
 *  GÖVDEDE (opak `PersonnelEntry` jetonu; güvenlik kamerayla/elle okur, sunucu çözer). */
export interface EntryVerifyRequest {
  qrToken: string;
}

/**
 * Personel QR doğrulama yanıtı — §29.5 (`POST /security/entry/verify`, Akış A). Jetondan
 * personel/saha çözülür → uygunluk + geçiş logu (`Method=PersonnelQr`) + jeton TÜKETİLİR
 * (`PersonnelEntry` tek-kullanımdır, §29.2 — ikinci verify → `allowed=false` + `reason=
 * 'TokenConsumed'`). `reason` İKİ aileden olabilir: izin ailesi (`SecurityPermitDenyReason`)
 * YA DA jeton ailesi (`SecurityTokenDenyReason` — `TokenExpired`/`TokenConsumed`/`TokenInvalid`);
 * `allowed=true` iken DAİMA `null`. `TcLookupResultDto`'nun aksine `found` YOKTUR — jeton ya
 * çözülür ya çözülmez (`TokenInvalid`); çözülemeyen/başka firma jetonunda `personnelName`/
 * `contractorName` de `null` (KVKK — jeton çözülmezse personel hiç bilinmez, §29.5).
 * `contractorName` **YENİ (S32c)** — ortak `null` kuralı `security.ts` dosya başı notunda.
 * **KVKK:** yanıt yalnız **ad + işveren + karar** taşır (TC/telefon YOK).
 */
export interface EntryVerifyResultDto {
  allowed: boolean;
  personnelName: string | null;
  contractorName: string | null;
  permitTitle: string | null;
  siteName: string | null;
  reason: SecurityPermitDenyReason | SecurityTokenDenyReason | null;
}

/** Kapı kodu yoklama isteği — §29.5 (`POST /security/entry/gate-result`). `qrToken` GÖVDEDE. */
export interface GateResultRequest {
  qrToken: string;
}

/**
 * Yoklama durumu — §29.5. Backend önceliği (normatif): `Unknown` > `Scanned`
 * (denetim satırı varsa) > `Expired` > `Pending`. `Unknown` `404` DEĞİL `200`'dür (jetonun
 * varlığı sızmaz — 256-bit entropi oracle üretmez).
 */
export type GateResultStatus = 'Pending' | 'Scanned' | 'Expired' | 'Unknown';

/**
 * İzin penceresinin SUNUCU saatindeki konumu — §29.1(2)/§29.5 (`GateScanPermitDto.validityState`,
 * YENİ S32f, normatif). `entryPermitted` bir HİBEDİR (§29.1'in 4. koşulu); bu alan 2. koşulu
 * (`validityStart ≤ now ≤ validityEnd`) TAŞIR — ikisi ÇÖKERTİLMEZ. Sınırlar DÂHİLDİR
 * (`now === validityStart` → `Current`). `now`, sunucunun yanıtı ürettiği andır; `gate-result`
 * yolunda yanıt anı, detay yolunda `liveAsOf`'un TA KENDİSİ (aynı damga). İstemci bu pencereyi
 * KENDİ SAATİYLE hesaplamaz (mülkiyet kuralı, §29.5) — alan HER ZAMAN doludur, türetilmez.
 */
export type PermitValidityState = 'Current' | 'NotStarted' | 'Ended';

/** Hibenin ÜÇ hâli — §29.5 S32g madde 8 (YENİ). `entryPermitted`'ı DARALTMAZ:
 *  `entryPermitted === (entryGrantState === 'Granted')` DAİMA doğrudur (bilinçli fazlalık).
 *  `Pending` = hiç verilmedi; `Revoked` = verildi, sonra geri alındı — `WorkPermitPersonnel`'in
 *  YENİ `EntryRevokedAt`/`EntryRevokedByUserId` kolonlarından türetilir. */
export type PermitEntryGrantState = 'Granted' | 'Pending' | 'Revoked';

/**
 * Kapı uygunluk kontrol listesinin (S32g) `readiness` bloğu — §29.5 S32g madde 3/4/9/10 (YENİ).
 * `entry-readiness` ALAN KAPISININ arkasında (kendi ucu YOKTUR — `gate-result` grant'ı yeterli
 * DEĞİLDİR); grant yokken `GateScanPermitDto.readiness` bütünüyle `null` gelir. **`null` "eksik
 * yok" DEĞİL, "bilinmiyor" demektir** — istemci `null` iken satır 1/2'yi HİÇ göstermez (`—` ile
 * bile). Yetki VARKEN blok DAİMA doludur (alanları tek tek nullable DEĞİLDİR); "eksik yok" BOŞ
 * dizilerle anlatılır.
 *
 * **Durum satırı BOOLEAN'dan okunur, liste UZUNLUĞUNDAN ASLA (§29.5 madde 4).**
 * `trainingsComplete = false` + `missingTrainingTitles = []` MEŞRU bir birleşimdir (çözülemeyen
 * eğitim id'si sessizce düşürülür) — "eksik yok" DEĞİL, "eksik var, adı gösterilemiyor" demektir.
 * Aynı kural belge tarafında simetriktir.
 */
export interface GatePermitReadinessDto {
  /** Kontrol listesi satır 1'in (`Zorunlu Evraklar`) DURUMU — TEK kaynak. */
  requiredDocumentsSatisfied: boolean;
  /** Eksik türlerin §27 `personnel` kayıt kodları (PascalCase). ASLA `null`, boş OLABİLİR. */
  missingDocumentTypes: string[];
  /** Kontrol listesi satır 2'nin (`İSG Eğitimleri`) DURUMU — TEK kaynak. */
  trainingsComplete: boolean;
  /** Eksik eğitimlerin BAŞLIKLARI — uuid YOK (§29.5 derin-bağlantısızlık kuralı). ASLA `null`. */
  missingTrainingTitles: string[];
}

/**
 * Okutulan sahaya KAPSANMIŞ izin satırı — §29.5 (`GateScanPermitDto`). CANLI sorgu
 * (yoklama anında hesaplanır); terminal-negatif izinler (`Rejected`/`Closed`) kapsam DIŞIDIR.
 * Pencere dışı izinler de bu listede KALIR — sunucu SÜZMEZ (§29.5, S32f normatif not); bir
 * `OutOfValidity` reddinin tek açıklanabilir kanıtı budur.
 *
 * **Son üç alan YENİ (S32g) — kapı uygunluk kontrol listesi (api-contract §29.5 S32g bloğu,
 * 14 madde).** Tamamen EKLEMELİDİR; üstteki altı alan DEĞİŞMEDİ. **Backend bu dilimi henüz
 * DAĞITMADIYSA üçü de `undefined` gelir** — bu `false`/`null` DEĞİLDİR ve öyle OKUNMAZ
 * (`contractorName` S32c'nin AYNI dersi): `apps/web-expert/src/lib/eligibility.ts` üç alanı da
 * `undefined`'a karşı AÇIKÇA sınar, kontrol listesi o durumda TAMAMEN atlanır — üç kırmızı satır
 * ASLA render EDİLMEZ.
 */
export interface GateScanPermitDto {
  title: string;
  siteName: string;
  /** UTC ISO 8601 (§4). */
  validityStart: string;
  /** UTC ISO 8601 (§4). */
  validityEnd: string;
  /** BİR HİBE — `WorkPermitPersonnel.EntryPermittedAt` dolu mu. `validityState` İLE BİRLİKTE
   *  okunur; tek başına "girebilir" demez (§29.1, §29.5 S32f normatif not). */
  entryPermitted: boolean;
  /** YENİ (S32f) — bkz. `PermitValidityState` üstündeki not. */
  validityState: PermitValidityState;
  /** YENİ (S32g) — bkz. `PermitEntryGrantState` üstündeki not. Backend dağıtılmadıysa `undefined`. */
  entryGrantState?: PermitEntryGrantState;
  /** YENİ (S32g) — §29.5 madde 6/7: "kontrol listesinin ANLATTIĞI izin". Sunucu `permits[]`
   *  içinde EN ÇOK BİR kalemi `true` işaretler (boşsa hiçbiri) — istemci bu BAYRAĞI okur, İNDEKSİ
   *  ASLA varsaymaz; `title` bir ETİKETTİR, arama anahtarı DEĞİL (iki izin aynı başlığı taşıyabilir).
   *  Bir NEDENSELLİK İDDİASI DEĞİLDİR — `at`'e bakmaz, `result`/`reason` ile ilişki KURMAZ. Backend
   *  dağıtılmadıysa `undefined` (== `false` DEĞİL — kontrol listesi o zaman hiç render EDİLMEZ). */
  closestToEntry?: boolean;
  /** YENİ (S32g) — bkz. `GatePermitReadinessDto` üstündeki not. Backend dağıtılmadıysa `undefined`;
   *  dağıtıldıktan sonra `entry-readiness` grant'ı yoksa `null` ("bilinmiyor", "eksik yok" DEĞİL). */
  readiness?: GatePermitReadinessDto | null;
}

/**
 * Tek bir okutma — §29.5 (`GateScanDto`). `allowed`/`reason` OKUTMA ANINDA DONMUŞ bir
 * OLAYDIR; `permits` YOKLAMA ANINDA CANLI bir DURUMDUR — ikisi görünür biçimde çelişebilir
 * (bilinçli, §29.5 normatif not — İSG uzmanı arada `EntryPermittedAt`'i geri alabilir).
 * `method` bu uçta DAİMA `'GateQr'` (kapı kodu ile okutuldu).
 */
export interface GateScanDto {
  /** UTC ISO 8601 (§4) — okutma anı (pairing/merge anahtarının bir parçası). */
  scannedAt: string;
  personnelName: string;
  /** YENİ (S32c) — bkz. `TcLookupResultDto.contractorName` üstündeki normatif not; kural
   *  dört uçta ORTAK. CANLI çözülür (log'a YAZILMAZ) — aynı günün eski satırları bağ gün
   *  içinde değişirse YENİ adı gösterebilir (bilinçli, §29.5). */
  contractorName: string | null;
  allowed: boolean;
  reason: SecurityPermitDenyReason | null;
  siteName: string | null;
  method: SecurityEntryMethod;
  permits: GateScanPermitDto[];
}

/** Kapı kodu yoklama yanıtı — §29.5 (`GateResultDto`). */
export interface GateResultDto {
  status: GateResultStatus;
  /**
   * SUNUCU belirler — dolu iken bu saniye sonra yeniden yokla, **`null` iken yoklama
   * DURUR**. İstemci kendi aralığını/zaman aşımını İCAT ETMEZ (§1/§20.1 konvansiyonu).
   */
  pollAfterSeconds: number | null;
  /** Bugün SABİT `'In'` (§29.3) — okunur, ekranda GÖSTERİLMEZ (plan K6 sigortası). */
  direction: 'In';
  /** ASLA `null`; okutma yoksa `[]` (en çok 50 — §29.2 okutma sınırı). */
  scans: GateScanDto[];
}

/**
 * `result` süzgeci — §29.5 (YENİ, S32b). `allowed` (VARSAYILAN) = yalnız içeri alınanlar
 * (tahliye yoklaması üst-kümesi); `all` = aynı gün + aynı sahanın TÜM denemeleri
 * (`Allowed` + `Denied`), ayırt eden alan satırın `result`'ı. Üçüncü bir değer (`denied`)
 * BİLEREK YOKTUR (§29.5 normatif not) — tanınmayan bir değer VARSAYILANA (`allowed`) çözülür,
 * `all`'a DEĞİL (§20.3 `?status=` konvansiyonuyla birebir).
 */
export type SecurityEntriesResultFilter = 'allowed' | 'all';

/**
 * Günlük geçiş satırı — §29.5 (`GET /security/entries`). S32b ÖNCESİ yalnız `Result=Allowed`
 * dönerdi (dört alan); S32b `result=all` ile REDDEDİLENLERİ de AYNI listeye ekledi — ayırt eden
 * alan `result`. `direction` bir SONUÇ DEĞİLDİR (bugün sabit `'In'`, bir `Denied` satırı da
 * `direction='In'` taşır — "içeri girme DENEMESİ"); geçti/geçemedi ayrımı YALNIZ `result`'tan
 * okunur.
 */
export interface SecurityEntryRowDto {
  /** YENİ (S32d) — satırın OPAK kimliği (sıralı iç `Id` DEĞİL). Sıralamada/gruplamada
   *  KULLANILMAZ (sıra hâlâ `at` + iç `Id`'dendir) — yalnız `GET /security/entries/{uuid}`'nin
   *  ADRESİDİR. İstemci bunu ÜRETMEZ/TAHMİN ETMEZ, yalnız bu listeden ECHO eder. */
  uuid: string;
  /** UTC ISO 8601 (§4) — DENEME anı. */
  at: string;
  personnelName: string;
  /** YENİ (S32c) — bkz. `TcLookupResultDto.contractorName` üstündeki normatif not; kural
   *  dört uçta ORTAK. CANLI çözülür (log'a YAZILMAZ). */
  contractorName: string | null;
  /** Bugün SABİT `'In'` (§29.3) — bir SONUÇ DEĞİL, denemenin YÖNÜ. */
  direction: 'In';
  /** ZORUNLU — bkz. dosya üstü `SecurityEntryMethod` notu; bu liste bir tahliye yoklamasıdır. */
  method: SecurityEntryMethod;
  /** YENİ (S32b) — `SiteEntryLog.Result`'ın kablo değeri. Sayım kuralı (normatif): YALNIZ
   *  `'Allowed'` satırları bir fiziksel varlık iddiası taşır; `'Denied'` HİÇBİR sayımda SAYMAZ. */
  result: 'Allowed' | 'Denied';
  /**
   * YENİ (S32b) — YALNIZ `result='Denied'` iken dolu (`Allowed`'ta DAİMA `null`). §29.1'in İKİ
   * ailesinden biri olabilir: izin ailesi (`NoPermit`/`OutOfSite`/`EntryNotPermitted`/
   * `OutOfValidity`/`Terminal`) YA DA jeton ailesi (`TokenExpired`/`TokenConsumed`) —
   * `TokenInvalid` bu uçta ASLA görünmez (çözülemeyen jetonda satır hiç yazılmaz). Tip kasıtlı
   * olarak geniş (`string`): bilinmeyen/gelecekte eklenecek bir kod NÖTR bir yedek metne düşer,
   * istemci asla bir izin ifadesine düşürmez (§29.1 kırıcı-değişiklik kuralı) ve çökmez.
   */
  reason: string | null;
}

/**
 * `GET /security/entries/{uuid}` yanıtı — §29.5 (`SecurityEntryDetailDto`, YENİ S32d). Gün
 * listesinin BİR satırının salt-okuma detayı: iki yarı, iki farklı disiplin.
 *
 * **DONMUŞ yarı** (`uuid`/`at`/`direction`/`method`/`result`/`reason`) — `SiteEntryLog`'tan
 * OLDUĞU GİBİ, satırla BİREBİR aynı sekiz alanın altısı; bir OLAYDIR, bir daha ASLA değişmez.
 * `personnelName`/`contractorName` de satırla aynı alanlardır ama CANLI çözülür (aşağı bakınız)
 * — bu ikisi hem "tekrar" hem "canlı" olabilir çünkü kaynakları (kimlik çözümü) her okumada
 * yeniden çalışır, yalnız değeri genelde DEĞİŞMEZ.
 *
 * **CANLI yarı** (`personnelName`/`contractorName`/`siteName`/`permits`) — okuma ANINDA
 * çözülür ve `liveAsOf` ile damgalanır. `permits[]` geçmişteki hâl DEĞİLDİR (normatif, §29.5) —
 * bir izin başlığı/geçerlilik penceresi yerinde ÜZERİNE YAZILIR ve `EntryPermittedAt` geri
 * alınınca `null`'a düşer; "o an giriş izni var mıydı"nın kanıtı SİLİNİR. Bu yüzden istemci
 * **`permits[]` bloğunu DAİMA `liveAsOf` ile etiketler, ASLA `at` ile** — etiketsiz bir liste
 * geçmişteki hâl gibi OKUNUR, bu bir biçim tercihi değil bir kayıt-bütünlüğü kuralıdır.
 * `result === 'Allowed'` VE hiçbir kalemde `entryPermitted === true` yoksa (boş liste DÂHİL)
 * nötr bir uyuşmazlık notu gösterilir (`security.verdict.liveMismatch`) — bu bir hata DEĞİLDİR.
 *
 * Reddedilen bir denemede **"hangi izin engelledi" sorusu CEVAPLANAMAZ** — `SiteEntryLog.PermitId`
 * yalnız `Allowed` satırlarında dolar (`docs/BACKLOG.md:444`); istemci bu iddiayı ASLA kurmaz.
 *
 * Yan etkisizdir (GET, log YAZMAZ, jetona DOKUNMAZ, sayaç DEĞİŞTİRMEZ); önbelleğe ALINMAZ —
 * modal her açılışta yeniden çeker. Yetki AYNI aksiyondur (`security:gate-result`, yeni grant
 * YOK). `siteUuid` ALMAZ (satırın sahası kaydın kendisindedir) ve `permitUuid`/`contractorUuid`
 * DÖNMEZ (§29.5'in derin-bağlantısız kuralı — bkz. dosya başı notu).
 */
export interface SecurityEntryDetailDto {
  uuid: string;
  /** UTC ISO 8601 (§4) — DENEME anı (donmuş). */
  at: string;
  direction: 'In';
  method: SecurityEntryMethod;
  result: 'Allowed' | 'Denied';
  /** YALNIZ `result='Denied'`; §29.1'in İKİ ailesinden biri olabilir (satırla AYNI genişlik). */
  reason: string | null;
  personnelName: string;
  contractorName: string | null;
  /** Denemenin sahası; `SiteId` yok / saha silinmiş → `null`. Bu ucun `siteUuid` DÖNMEZ. */
  siteName: string | null;
  /** ASLA `null`; yalnız denemenin SAHASINA kapsanmış, CANLI liste. `gate-result` ile BİREBİR
   *  aynı şekil (`GateScanPermitDto`) — paralel bir tip İCAT EDİLMEDİ. */
  permits: GateScanPermitDto[];
  /** UTC ISO 8601 (§4) — SUNUCUNUN canlı yarıyı okuduğu an. İstemci `permits[]`'i BUNUNLA
   *  etiketler, `at` İLE DEĞİL (yukarıdaki dosya-üstü not). */
  liveAsOf: string;
}

/** `GET /security/entries` parametreleri — §29.5. `siteUuid` ZORUNLU, `date`/`result` opsiyonel. */
export interface SecurityEntriesParams {
  siteUuid: string;
  /** `yyyy-MM-dd` (§4 `DateOnly`) — verilmezse çağıranın (yerel zon) bugünü. */
  date?: string;
  /** YENİ (S32b) — verilmezse sunucu `allowed`'a çözer (dosya üstü `SecurityEntriesResultFilter` notu). */
  result?: SecurityEntriesResultFilter;
}

// ============================================================================
// Uçlar (api-contract §29.5)
// ============================================================================

/**
 * `GET /security/sites` (`get`) — güvenliğin kendi firmasının saha listesi (ada göre
 * sıralı). Firmanın sahası yoksa boş liste (`[]`, hâlâ `200`). Bu uç aynı zamanda erişim
 * sondası olarak kullanılır (`useSecurityAccess`) — PII taşımaz.
 */
export function listSites(): Promise<SecuritySiteDto[]> {
  return api.get<SecuritySiteDto[]>('security/sites');
}

/**
 * `POST /security/entry-qr` (`gate-qr`) — kapı/saha QR'ı üretir (`GateEntry`, TTL boyunca
 * ÇOK-KULLANIMLI). Saha yok/başka firma → `404 SITE_NOT_FOUND`.
 */
export function createGateQr(body: CreateGateQrRequest): Promise<EntryTokenDto> {
  return api.post<EntryTokenDto>('security/entry-qr', { json: body });
}

/**
 * `POST /security/entry/verify` (`verify`, Akış A — brief 092) — personelin `PersonnelEntry`
 * giriş QR'ını DOĞRULAR: güvenlik kamerayla (ya da elle yapıştırmayla) okur, jeton GÖVDEDE gider.
 * Jeton TÜKETİLİR (tek-kullanım, §29.2) + geçiş logu yazılır (`Method=PersonnelQr`, yan-etkili →
 * POST doğru). Süresi geçmiş → `reason='TokenExpired'`; zaten okunmuş → `TokenConsumed`; geçersiz/
 * başka firma → `TokenInvalid` (ad/işveren de `null`, cross-firma sızıntısı yok). Firma yok →
 * `403 COMPANY_NOT_ACCESSIBLE`.
 */
export function verify(body: EntryVerifyRequest): Promise<EntryVerifyResultDto> {
  return api.post<EntryVerifyResultDto>('security/entry/verify', { json: body });
}

/**
 * `POST /security/entry/tc-lookup` (`tc-lookup`) — TC ile sorgu (kamerasız/kodsuz yedek
 * yol). `tc` GÖVDEDE (KVKK — asla query string'e girmez). Saha yok/başka firma →
 * `404 SITE_NOT_FOUND`; `tc` boş → `400 VALIDATION_FAILED`.
 */
export function tcLookup(body: TcLookupRequest): Promise<TcLookupResultDto> {
  return api.post<TcLookupResultDto>('security/entry/tc-lookup', { json: body });
}

/**
 * `POST /security/entry/gate-result` (`gate-result`, YENİ — S32) — kapı kodunun sonucunu
 * YOKLAR: kimin okuttuğu, kararı, izinleri. Jetonu TÜKETMEZ, log YAZMAZ (yan etkisiz —
 * dönme turunda eski+yeni jeton birlikte güvenle yoklanabilir, bkz. `useGateCode.ts`).
 * `403 FORBIDDEN` → aksiyon grant'sız (fail-closed, K4) — 403 COMPANY_NOT_ACCESSIBLE'dan
 * AYRIŞTIRILMALIDIR (istemci `errorCode`'a bakar).
 */
export function gateResult(body: GateResultRequest): Promise<GateResultDto> {
  return api.post<GateResultDto>('security/entry/gate-result', { json: body });
}

/**
 * `GET /security/entries` (`gate-result`, YENİ — S32; `result` YENİ — S32b) — bir sahanın o
 * günkü geçiş DENEMELERİ. `result` verilmezse yalnız içeri alınanlar (tahliye yoklaması
 * üst-kümesi); `result: 'all'` ile reddedilenler de AYNI listeye girer (satır başına `result`).
 * `page`/`pageSize`/`search`/`sortBy` bu uçta YOKTUR (§3 sayfalaması geçerli değildir) — bunun
 * yerine zarf `total`/`filteredTotal` taşır (§3): `total` = süzgeç ÖNCESİ (Allowed+Denied),
 * `filteredTotal` = süzgeç SONRASI + kısıttan ÖNCE. `Denied` satırları en yeni 200 ile
 * sınırlıdır (normatif) — `value.length < filteredTotal` kesilmeyi gösterir, çağıran bunu
 * GÖSTERMEK ZORUNDADIR (§29.5). `api.list` bu üçünü `PagedResult` olarak ayrıştırır.
 */
export function listEntries(
  params: SecurityEntriesParams,
): Promise<PagedResult<SecurityEntryRowDto>> {
  return api.list<SecurityEntryRowDto>('security/entries', {
    searchParams: { siteUuid: params.siteUuid, date: params.date, result: params.result },
  });
}

/**
 * `GET /security/entries/{uuid}` (`gate-result`, YENİ — S32d) — gün listesinin BİR satırının
 * salt-okuma detayı (bkz. `SecurityEntryDetailDto` üstündeki normatif not). `uuid` YALNIZ
 * `listEntries`'ten gelen bir satırdan ECHO edilir — istemci üretmez/tahmin etmez. Başka
 * firmanın satırı / yok → `404 ENTRY_NOT_FOUND` (ikisi ayırt edilemez, cross-tenant sızmaz).
 * `siteUuid` ALMAZ. Yan etkisiz — önbelleğe alınmaması çağıranın sorumluluğudur (TanStack Query
 * tarafı, `apps/web-expert/src/lib/securityKeys.ts`).
 */
export function getEntryDetail(uuid: string): Promise<SecurityEntryDetailDto> {
  return api.get<SecurityEntryDetailDto>(`security/entries/${uuid}`);
}
